import { useState, useEffect, useCallback, useRef } from 'react';
import {
  MapPin, ArrowRightLeft, ClipboardList, Plus, CheckCircle2, RefreshCw, Filter, X,
} from 'lucide-react';
import { visitsApi } from '../../api/visits.js';
import { usersApi } from '../../api/users.js';
import { leadsApi } from '../../api/leads.js';
import { rawCustomersApi } from '../../api/customers.js';

const inp = {
  padding: '7px 11px', border: '1px solid #d1d5db', borderRadius: 7,
  fontSize: '0.84rem', background: '#fff', outline: 'none',
  color: '#111827', boxSizing: 'border-box',
};
const thStyle = {
  padding: '10px 14px', textAlign: 'left', fontWeight: 600,
  fontSize: '0.75rem', color: '#6b7280', whiteSpace: 'nowrap',
  textTransform: 'uppercase', letterSpacing: '0.04em',
};
const tdStyle = { padding: '11px 14px', fontSize: '0.85rem', verticalAlign: 'middle' };

const VISIT_STATUS_COLORS = {
  open:        { bg: '#dcfce7', color: '#166534' },
  temp_closed: { bg: '#fef9c3', color: '#854d0e' },
  perm_closed: { bg: '#fee2e2', color: '#991b1b' },
  not_found:   { bg: '#f3f4f6', color: '#6b7280' },
};
const VISIT_STATUS_LABELS = {
  open: 'Open', temp_closed: 'Temp Closed',
  perm_closed: 'Perm Closed', not_found: 'Not Found',
};
const OUTCOME_LABELS = {
  order_placed: 'Order Placed', ready_to_order: 'Ready to Order',
  pending: 'Pending', follow_up: 'Follow Up',
  non_user: 'Non User', not_interested: 'Not Interested', other: 'Other',
};
const PAGE_LIMITS = [20, 40, 80];

const Badge = ({ bg, color, children }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: '0.72rem', fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: bg, color, whiteSpace: 'nowrap' }}>{children}</span>
);

function Pagination({ page, pages, limit, onLimitChange, onChange }) {
  return (
    <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', marginTop: '1rem', flexWrap: 'wrap' }}>
      <button onClick={() => onChange(page - 1)} disabled={page <= 1} style={{ ...inp, cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? 0.4 : 1, padding: '5px 14px' }}>←</button>
      {pages > 0 && <span style={{ fontSize: '0.83rem', color: '#6b7280', padding: '0 4px' }}>Page {page} of {pages}</span>}
      <button onClick={() => onChange(page + 1)} disabled={page >= pages} style={{ ...inp, cursor: page >= pages ? 'not-allowed' : 'pointer', opacity: page >= pages ? 0.4 : 1, padding: '5px 14px' }}>→</button>
      <select value={limit} onChange={e => { onLimitChange(Number(e.target.value)); onChange(1); }} style={{ ...inp, marginLeft: 8, padding: '5px 8px', cursor: 'pointer' }}>
        {PAGE_LIMITS.map(l => <option key={l} value={l}>{l} / page</option>)}
      </select>
    </div>
  );
}

const FilterLabel = ({ children }) => (
  <span style={{ fontSize: '0.71rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 5 }}>
    {children}
  </span>
);

const NEW_CHIPS = [
  { val: 'HOT',    label: 'Hot',    bg: '#fef2f2', color: '#dc2626', border: '#fca5a5' },
  { val: 'MEDIUM', label: 'Medium', bg: '#fffbeb', color: '#d97706', border: '#fcd34d' },
  { val: 'COLD',   label: 'Cold',   bg: '#eff6ff', color: '#2563eb', border: '#93c5fd' },
];
const LEAD_CHIPS = [
  { val: 'green ++', label: 'Green++', bg: '#dcfce7', color: '#15803d', border: '#86efac' },
  { val: 'green',    label: 'Green',   bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
  { val: 'yellow',   label: 'Yellow',  bg: '#fefce8', color: '#ca8a04', border: '#fde047' },
  { val: 'red',      label: 'Red',     bg: '#fef2f2', color: '#dc2626', border: '#fca5a5' },
];

// ─── AddVisitModal ────────────────────────────────────────────────────────────
function AddVisitModal({ salesUsers, onClose, onSuccess }) {
  const today = new Date().toISOString().slice(0, 10);

  const [visitType, setVisitType] = useState('new');

  // Lead / Customer search
  const [leadQuery, setLeadQuery] = useState('');
  const [leadOptions, setLeadOptions] = useState([]);
  const [leadLoading, setLeadLoading] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [dropOpen, setDropOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');

  // Salesperson searchable select
  const [salesSearch, setSalesSearch] = useState('');
  const [salesDropOpen, setSalesDropOpen] = useState(false);
  const [selectedSales, setSelectedSales] = useState(null);

  const [visitDate, setVisitDate] = useState(today);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const debRef = useRef(null);
  const dropRef = useRef(null);
  const salesDropRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    const h = (e) => { if (salesDropRef.current && !salesDropRef.current.contains(e.target)) setSalesDropOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // Reset entity search when visit type changes
  useEffect(() => {
    setSelectedLead(null);
    setLeadQuery('');
    setLeadOptions([]);
    setTypeFilter('');
    setDropOpen(false);
  }, [visitType]);

  const searchEntities = (q, filter) => {
    setLeadLoading(true);
    clearTimeout(debRef.current);
    debRef.current = setTimeout(async () => {
      try {
        if (visitType === 'new') {
          const params = { search: q, limit: 15 };
          if (filter) params.lead_score = filter;
          const res = await rawCustomersApi.getAll(params);
          const list = res?.data || [];
          setLeadOptions(Array.isArray(list) ? list : []);
        } else {
          const params = { search: q, limit: 15 };
          if (filter) params.status_flag = filter;
          const res = await leadsApi.getAll(params);
          const list = res?.data || [];
          setLeadOptions(Array.isArray(list) ? list : []);
        }
      } catch { setLeadOptions([]); }
      finally { setLeadLoading(false); }
    }, 280);
  };

  const handleLeadInput = (e) => {
    const q = e.target.value;
    setLeadQuery(q);
    setSelectedLead(null);
    setDropOpen(true);
    if (q.length >= 1) searchEntities(q, typeFilter);
    else setLeadOptions([]);
  };

  const handleChipClick = (val) => {
    const next = typeFilter === val ? '' : val;
    setTypeFilter(next);
    setDropOpen(true);
    searchEntities(leadQuery, next);
  };

  const pickLead = (item) => {
    setSelectedLead(item);
    setLeadQuery(item.name);
    setDropOpen(false);
  };

  const filteredSales = salesSearch
    ? salesUsers.filter(u => u.name.toLowerCase().includes(salesSearch.toLowerCase()))
    : salesUsers;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedLead)  { setError('Select a lead / customer.'); return; }
    if (!selectedSales) { setError('Select a salesperson.'); return; }
    if (!visitDate)     { setError('Select a visit date.'); return; }
    setSubmitting(true); setError('');
    try {
      const payload = {
        sales_id:   selectedSales.id,
        type:       visitType,
        visit_date: visitDate,
        status:     'pending',
        note:       note || null,
      };
      if (visitType === 'new') {
        payload.raw_customer_list_id = selectedLead.id;
      } else {
        payload.customer_id = selectedLead.id;
      }
      await visitsApi.create(payload);
      onSuccess('Visit added successfully.');
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to add visit.');
      setSubmitting(false);
    }
  };

  const isNew = visitType === 'new';
  const chips = isNew ? NEW_CHIPS : LEAD_CHIPS;
  const entityLabel = isNew ? 'New Customer *' : 'Lead *';

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(17,24,39,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
      onMouseDown={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 480, boxShadow: '0 25px 60px rgba(0,0,0,0.22)', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.1rem 1.4rem', borderBottom: '1px solid #f3f4f6', flexShrink: 0 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.98rem', color: '#111827' }}>Add Visit</div>
            <div style={{ fontSize: '0.78rem', color: '#9ca3af', marginTop: 2 }}>Manually schedule a visit for a salesperson</div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: '#f3f4f6', borderRadius: 8, width: 30, height: 30, cursor: 'pointer', color: '#6b7280', fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '1.4rem', display: 'flex', flexDirection: 'column', gap: '1rem', overflowY: 'auto' }}>

          {/* Visit type — first so it drives the search below */}
          <div>
            <FilterLabel>Visit Type</FilterLabel>
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
              {[{ value: 'new', label: 'New' }, { value: 'follow_up', label: 'Follow Up' }, { value: 'payment', label: 'Payment' }].map(t => (
                <label key={t.value} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 13px', border: `1.5px solid ${visitType === t.value ? '#3b82f6' : '#e5e7eb'}`, borderRadius: 7, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 500, background: visitType === t.value ? '#eff6ff' : '#fff', color: visitType === t.value ? '#1d4ed8' : '#374151', userSelect: 'none' }}>
                  <input type="radio" name="visit_type" value={t.value} checked={visitType === t.value} onChange={() => setVisitType(t.value)} style={{ display: 'none' }} />
                  {t.label}
                </label>
              ))}
            </div>
          </div>

          {/* Lead / Customer search — label + chips change based on visitType */}
          <div>
            <FilterLabel>{entityLabel}</FilterLabel>
            {/* Filter chips */}
            <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginBottom: 7 }}>
              {chips.map(c => (
                <button key={c.val} type="button" onClick={() => handleChipClick(c.val)}
                  style={{ padding: '3px 10px', borderRadius: 20, fontSize: '0.71rem', fontWeight: 600, cursor: 'pointer', border: `1.5px solid ${typeFilter === c.val ? c.border : '#e5e7eb'}`, background: typeFilter === c.val ? c.bg : '#f9fafb', color: typeFilter === c.val ? c.color : '#9ca3af', transition: 'all 0.15s' }}>
                  {c.label}
                </button>
              ))}
              {typeFilter && (
                <button type="button" onClick={() => { setTypeFilter(''); if (leadQuery) searchEntities(leadQuery, ''); }}
                  style={{ padding: '3px 9px', borderRadius: 20, fontSize: '0.71rem', fontWeight: 600, cursor: 'pointer', border: '1.5px solid #e5e7eb', background: '#fff', color: '#6b7280' }}>
                  ✕ Clear
                </button>
              )}
            </div>
            <div ref={dropRef} style={{ position: 'relative' }}>
              <input
                style={{ ...inp, width: '100%' }}
                placeholder="Search by name or phone…"
                value={leadQuery}
                onChange={handleLeadInput}
                onFocus={() => { setDropOpen(true); if (!leadQuery && typeFilter) searchEntities('', typeFilter); }}
                autoComplete="off"
              />
              {dropOpen && (
                <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 200, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 9, boxShadow: '0 8px 24px rgba(0,0,0,0.13)', maxHeight: 200, overflowY: 'auto' }}>
                  {leadLoading ? (
                    <div style={{ padding: '10px 14px', fontSize: '0.83rem', color: '#9ca3af' }}>Searching…</div>
                  ) : leadOptions.length === 0 ? (
                    <div style={{ padding: '10px 14px', fontSize: '0.83rem', color: '#9ca3af' }}>{leadQuery || typeFilter ? 'No results.' : 'Type or pick a filter to search…'}</div>
                  ) : leadOptions.map(l => (
                    <div key={l.id} onMouseDown={() => pickLead(l)}
                      style={{ padding: '9px 14px', cursor: 'pointer', fontSize: '0.85rem', borderBottom: '1px solid #f9fafb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}
                    >
                      <span style={{ fontWeight: 500 }}>{l.name}</span>
                      <span style={{ fontSize: '0.78rem', color: '#9ca3af' }}>{l.phone}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {selectedLead && (
              <div style={{ marginTop: 5, padding: '5px 10px', background: '#eff6ff', borderRadius: 6, fontSize: '0.8rem', color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>✓</span> <strong>{selectedLead.name}</strong> <span style={{ opacity: 0.65 }}>· {selectedLead.phone}</span>
              </div>
            )}
          </div>

          {/* Salesperson — searchable typeahead */}
          <div>
            <FilterLabel>Salesperson *</FilterLabel>
            <div ref={salesDropRef} style={{ position: 'relative' }}>
              <input
                style={{ ...inp, width: '100%' }}
                placeholder="Search salesperson…"
                value={salesDropOpen ? salesSearch : (selectedSales?.name || '')}
                onChange={e => { setSalesSearch(e.target.value); setSalesDropOpen(true); }}
                onFocus={() => { setSalesDropOpen(true); setSalesSearch(''); }}
                autoComplete="off"
              />
              {salesDropOpen && (
                <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 200, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 9, boxShadow: '0 8px 24px rgba(0,0,0,0.13)', maxHeight: 180, overflowY: 'auto' }}>
                  {filteredSales.length === 0 ? (
                    <div style={{ padding: '10px 14px', fontSize: '0.83rem', color: '#9ca3af' }}>No match.</div>
                  ) : filteredSales.map(u => (
                    <div key={u.id} onMouseDown={() => { setSelectedSales(u); setSalesDropOpen(false); setSalesSearch(''); }}
                      style={{ padding: '9px 14px', cursor: 'pointer', fontSize: '0.85rem', borderBottom: '1px solid #f9fafb', fontWeight: selectedSales?.id === u.id ? 600 : 400, background: selectedSales?.id === u.id ? '#eff6ff' : '' }}
                      onMouseEnter={e => { if (selectedSales?.id !== u.id) e.currentTarget.style.background = '#f8fafc'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = selectedSales?.id === u.id ? '#eff6ff' : ''; }}
                    >
                      {u.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Visit date */}
          <div>
            <FilterLabel>Visit Date *</FilterLabel>
            <input type="date" style={{ ...inp, width: '100%' }} value={visitDate} onChange={e => setVisitDate(e.target.value)} min={today} />
          </div>

          {/* Note */}
          <div>
            <FilterLabel>Note (optional)</FilterLabel>
            <textarea style={{ ...inp, width: '100%', resize: 'vertical', minHeight: 60 }} placeholder="Any additional info…" value={note} onChange={e => setNote(e.target.value)} />
          </div>

          {error && (
            <div style={{ padding: '9px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 7, fontSize: '0.82rem', color: '#991b1b' }}>{error}</div>
          )}

          <div style={{ display: 'flex', gap: '0.6rem', paddingTop: 2 }}>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: '9px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', cursor: 'pointer', fontWeight: 500, fontSize: '0.87rem', color: '#374151' }}>Cancel</button>
            <button type="submit" disabled={submitting || !selectedLead || !selectedSales}
              style={{ flex: 2, padding: '9px', border: 'none', borderRadius: 8, background: 'var(--primary)', opacity: (!selectedLead || !selectedSales || submitting) ? 0.5 : 1, color: '#fff', cursor: (!selectedLead || !selectedSales || submitting) ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.87rem' }}>
              {submitting ? 'Adding…' : '+ Add Visit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── VisitRemarksModal ────────────────────────────────────────────────────────
// Full remark (VisitLog) history for a single visit, paginated.
function VisitRemarksModal({ visit, onClose }) {
  const [remarks, setRemarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pages, setPages] = useState(1);
  const [count, setCount] = useState(0);

  useEffect(() => {
    setLoading(true);
    visitsApi.getLogs(visit.id, { page, limit })
      .then(res => {
        setRemarks(res.data?.data || []);
        setCount(res.data?.count || 0);
        setPages(res.data?.totalPages || 1);
      })
      .catch(() => setRemarks([]))
      .finally(() => setLoading(false));
  }, [visit.id, page, limit]);

  const customerName = visit.customer?.name ?? visit.raw_customer?.name ?? visit.corporate_customer?.name ?? '—';

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(17,24,39,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
      onMouseDown={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 680, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 60px rgba(0,0,0,0.22)' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.1rem 1.4rem', borderBottom: '1px solid #f3f4f6', flexShrink: 0 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#111827' }}>{customerName}</div>
            <div style={{ fontSize: '0.78rem', color: '#9ca3af', marginTop: 1 }}>
              Visit history{visit.visit_date ? ` — ${new Date(visit.visit_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}` : ''} · {count} remark{count === 1 ? '' : 's'}
            </div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: '#f3f4f6', borderRadius: 8, width: 30, height: 30, cursor: 'pointer', color: '#6b7280', fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>

        {/* Table */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: '#9ca3af', fontSize: '0.88rem' }}>Loading…</div>
          ) : remarks.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: '#9ca3af', fontSize: '0.88rem' }}>No remarks logged for this visit yet.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, zIndex: 1 }}>
                  {['Date', 'Visit Status', 'Outcome', 'Note', 'Logged By'].map(h => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {remarks.map(r => {
                  const sc = VISIT_STATUS_COLORS[r.visit_status] || { bg: '#f3f4f6', color: '#374151' };
                  return (
                    <tr key={r.id} style={{ borderBottom: '1px solid #f9fafb' }}>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap', color: '#9ca3af', fontSize: '0.8rem' }}>
                        {r.createdAt ? new Date(r.createdAt).toLocaleString('en-IN') : '—'}
                      </td>
                      <td style={tdStyle}><Badge bg={sc.bg} color={sc.color}>{VISIT_STATUS_LABELS[r.visit_status] ?? r.visit_status}</Badge></td>
                      <td style={tdStyle}>{r.outcome ? (OUTCOME_LABELS[r.outcome] ?? r.outcome) : '—'}</td>
                      <td style={{ ...tdStyle, color: '#374151' }}>{r.note || '—'}</td>
                      <td style={tdStyle}>{r.user?.name ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '10px 16px', borderTop: '1px solid #f3f4f6', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Pagination page={page} pages={pages} limit={limit} onLimitChange={setLimit} onChange={setPage} />
          <button onClick={onClose} style={{ padding: '7px 16px', border: '1px solid #e5e7eb', background: '#fff', borderRadius: 7, cursor: 'pointer', fontWeight: 500, fontSize: '0.84rem', color: '#374151' }}>Close</button>
        </div>
      </div>
    </div>
  );
}

export default function VisitAdminPage() {
  const today = new Date().toISOString().slice(0, 10);
  const [tab, setTab] = useState('visit-list');

  // ── Visit Logs tab state ────────────────────────────────────────────────────
  const [logs, setLogs] = useState([]);
  const [logCount, setLogCount] = useState(0);
  const [logPages, setLogPages] = useState(1);
  const [logPage, setLogPage] = useState(1);
  const [logLimit, setLogLimit] = useState(40);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logDateFrom, setLogDateFrom] = useState('');
  const [logDateTo, setLogDateTo] = useState('');
  const [logVStatus, setLogVStatus] = useState('');
  const [logOutcome, setLogOutcome] = useState('');
  const [logUserId, setLogUserId] = useState('');
  const [logCustomerFilter, setLogCustomerFilter] = useState(null); // { kind: 'lead'|'retail'|'corporate', id, name }

  // ── Visit List tab state ────────────────────────────────────────────────────
  const [reassignModal, setReassignModal] = useState(false);
  const [fromUser, setFromUser] = useState('');
  const [toUser, setToUser] = useState('');
  const [reassignDate, setReassignDate] = useState(today);
  const [reassignLoading, setReassignLoading] = useState(false);
  const [reassignResult, setReassignResult] = useState(null);
  const [salesUsers, setSalesUsers] = useState([]);
  const [addModal, setAddModal] = useState(false);
  const [addMsg, setAddMsg] = useState('');
  const [remarksModal, setRemarksModal] = useState(null); // visit object

  // Visit List — per-visit table (server-side filters + pagination)
  const [vlVisits, setVlVisits] = useState([]);
  const [vlLoading, setVlLoading] = useState(false);
  const [vlPage, setVlPage] = useState(1);
  const [vlLimit, setVlLimit] = useState(20);
  const [vlPages, setVlPages] = useState(1);
  const [vlCount, setVlCount] = useState(0);
  const [vlStats, setVlStats] = useState({});
  const [vlSalesId, setVlSalesId] = useState('');
  const [vlRetailSegment, setVlRetailSegment] = useState(''); // Dormant/XDifficult
  const [vlCustomerType, setVlCustomerType] = useState('');
  const [vlStatus, setVlStatus] = useState('');

  // ── Load sales users once ───────────────────────────────────────────────────
  useEffect(() => {
    const onlyActive = (users) => users.filter(u => u.is_active !== false);
    usersApi.getAll({ role: 'sales', limit: 200 })
      .then(res => {
        const list = res.data?.data || res.data || [];
        usersApi.getAll({ role: 'sales_tl', limit: 200 })
          .then(r2 => setSalesUsers(onlyActive([...list, ...(r2.data?.data || r2.data || [])])))
          .catch(() => setSalesUsers(onlyActive(list)));
      })
      .catch(() => setSalesUsers([]));
  }, []);

  // ── Fetch logs ──────────────────────────────────────────────────────────────
  const fetchLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const params = { page: logPage, limit: logLimit };
      if (logDateFrom)  params.date_from    = logDateFrom;
      if (logDateTo)    params.date_to      = logDateTo;
      if (logVStatus)   params.visit_status = logVStatus;
      if (logOutcome)   params.outcome      = logOutcome;
      if (logUserId)    params.user_id      = logUserId;
      if (logCustomerFilter) {
        if (logCustomerFilter.kind === 'lead')      params.lead_id                 = logCustomerFilter.id;
        if (logCustomerFilter.kind === 'retail')    params.raw_customer_list_id    = logCustomerFilter.id;
        if (logCustomerFilter.kind === 'corporate') params.corporate_customer_id   = logCustomerFilter.id;
      }
      const res = await visitsApi.adminAllLogs(params);
      setLogs(res.data?.data || []);
      setLogCount(res.data?.count || 0);
      setLogPages(res.data?.totalPages || 1);
    } catch { setLogs([]); }
    finally { setLogsLoading(false); }
  }, [logPage, logLimit, logDateFrom, logDateTo, logVStatus, logOutcome, logUserId, logCustomerFilter]);

  useEffect(() => { if (tab === 'logs') fetchLogs(); }, [tab, fetchLogs]);

  // ── Fetch visit list (per-visit, server-side filtered + paginated) ──────────
  const fetchVisitList = useCallback(async () => {
    setVlLoading(true);
    try {
      const params = { page: vlPage, limit: vlLimit };
      if (vlSalesId)       params.sales_id      = vlSalesId;
      if (vlRetailSegment) params.retail_segment = vlRetailSegment;
      if (vlCustomerType)  params.customer_type  = vlCustomerType;
      if (vlStatus)        params.status         = vlStatus;
      const res = await visitsApi.getAll(params);
      setVlVisits(res.data?.data || []);
      setVlCount(res.data?.count || 0);
      setVlPages(res.data?.totalPages || 1);
      setVlStats(res.data?.stats || {});
    } catch { setVlVisits([]); }
    finally { setVlLoading(false); }
  }, [vlPage, vlLimit, vlSalesId, vlRetailSegment, vlCustomerType, vlStatus]);

  useEffect(() => { if (tab === 'visit-list') fetchVisitList(); }, [tab, fetchVisitList]);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleReassign = async () => {
    if (!fromUser || !toUser) { alert('Select both users'); return; }
    if (fromUser === toUser)  { alert('From and To must be different'); return; }
    setReassignLoading(true);
    setReassignResult(null);
    try {
      const res = await visitsApi.adminReassign(fromUser, toUser, reassignDate);
      setReassignResult({ success: true, message: res.data?.message || 'Reassigned successfully' });
      fetchVisitList();
    } catch (e) {
      setReassignResult({ success: false, message: e?.response?.data?.message || e.message || 'Failed' });
    } finally { setReassignLoading(false); }
  };

  const hasVlFilters = vlSalesId || vlRetailSegment || vlCustomerType || vlStatus;

  const hasLogFilters = logDateFrom || logDateTo || logVStatus || logOutcome || logUserId || logCustomerFilter;

  return (
    <div className="page-body">

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ margin: 0, fontWeight: 700, fontSize: '1.2rem', color: '#111827' }}>Visit Management</h2>
          <p style={{ margin: '3px 0 0', fontSize: '0.83rem', color: '#9ca3af' }}>Monitor visit logs and manage daily sales visit queue.</p>
        </div>
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
          <button
            className="btn btn-outline"
            onClick={() => { setReassignModal(true); setReassignResult(null); }}
          >
            <ArrowRightLeft size={14} /> Reassign Visits
          </button>
          <button
            className="btn btn-primary"
            onClick={() => { setAddModal(true); setAddMsg(''); }}
          >
            <Plus size={15} /> Add Visit
          </button>
        </div>
      </div>

      {addMsg && (
        <div style={{ marginBottom: '1rem', padding: '9px 14px', background: 'var(--success-bg)', border: '1px solid #bbf7d0', borderRadius: 8, fontSize: '0.85rem', color: 'var(--success)', display: 'flex', gap: 8, alignItems: 'center' }}>
          <CheckCircle2 size={15} /> {addMsg}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '2px solid #e5e7eb', marginBottom: '1.25rem' }}>
        {[
          { key: 'visit-list', label: 'Visit List',  count: vlCount },
          { key: 'logs',       label: 'Visit Logs',  count: logCount },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '9px 20px', border: 'none', background: 'none', cursor: 'pointer',
            fontWeight: tab === t.key ? 700 : 500, fontSize: '0.88rem', marginBottom: -2,
            borderBottom: `2px solid ${tab === t.key ? '#3b82f6' : 'transparent'}`,
            color: tab === t.key ? '#3b82f6' : '#6b7280',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            {t.label}
            {t.count > 0 && (
              <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '1px 7px', borderRadius: 20, background: tab === t.key ? '#dbeafe' : '#f3f4f6', color: tab === t.key ? '#1d4ed8' : '#9ca3af' }}>{t.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── VISIT LOGS TAB ────────────────────────────────────────────────────── */}
      {tab === 'logs' && (
        <div>
          {/* Filters */}
          <div className="card" style={{ marginBottom: '1.25rem' }}>
            <div className="card-header">
              <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <Filter size={14} color="#6b7280" /> Filters
              </span>
              {hasLogFilters && (
                <button onClick={() => { setLogUserId(''); setLogVStatus(''); setLogOutcome(''); setLogDateFrom(''); setLogDateTo(''); setLogCustomerFilter(null); setLogPage(1); }}
                  className="btn btn-outline" style={{ padding: '5px 12px', fontSize: '0.78rem' }}>
                  <X size={12} /> Clear
                </button>
              )}
            </div>
            <div className="card-body">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'flex-start' }}>

              <div style={{ flex: '1 1 160px', minWidth: 140 }}>
                <FilterLabel>Salesperson</FilterLabel>
                <select style={{ ...inp, width: '100%' }} value={logUserId} onChange={e => { setLogUserId(e.target.value); setLogPage(1); }}>
                  <option value="">All</option>
                  {salesUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>

              <div style={{ flex: '1 1 140px', minWidth: 130 }}>
                <FilterLabel>Visit Status</FilterLabel>
                <select style={{ ...inp, width: '100%' }} value={logVStatus} onChange={e => { setLogVStatus(e.target.value); setLogPage(1); }}>
                  <option value="">All</option>
                  <option value="open">Open</option>
                  <option value="temp_closed">Temp Closed</option>
                  <option value="perm_closed">Perm Closed</option>
                  <option value="not_found">Not Found</option>
                </select>
              </div>

              <div style={{ flex: '1 1 150px', minWidth: 130 }}>
                <FilterLabel>Outcome</FilterLabel>
                <select style={{ ...inp, width: '100%' }} value={logOutcome} onChange={e => { setLogOutcome(e.target.value); setLogPage(1); }}>
                  <option value="">All</option>
                  {Object.entries(OUTCOME_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>

              <div style={{ flex: '1 1 260px', minWidth: 220 }}>
                <FilterLabel>Date Range</FilterLabel>
                <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                  <input type="date" style={{ ...inp, flex: 1 }} value={logDateFrom} onChange={e => { setLogDateFrom(e.target.value); setLogPage(1); }} />
                  <span style={{ color: '#9ca3af', fontSize: '0.8rem', flexShrink: 0 }}>to</span>
                  <input type="date" style={{ ...inp, flex: 1 }} value={logDateTo} onChange={e => { setLogDateTo(e.target.value); setLogPage(1); }} />
                </div>
              </div>

            </div>

            {logCustomerFilter && (
              <div style={{ marginTop: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: 6, background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', padding: '4px 10px', borderRadius: 20, fontSize: '0.8rem', fontWeight: 600 }}>
                Showing logs for: {logCustomerFilter.name || `Customer #${logCustomerFilter.id}`}
                <button
                  onClick={() => { setLogCustomerFilter(null); setLogPage(1); }}
                  style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', fontSize: '0.9rem', lineHeight: 1 }}
                >✕</button>
              </div>
            )}
            </div>
          </div>

          {/* Table */}
          <div className="card" style={{ overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                    {['#', 'Lead', 'Phone', 'Salesperson', 'Visit Status', 'Outcome', 'Products', 'Note', 'Next Visit', 'Date'].map(h => (
                      <th key={h} style={thStyle}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {logsLoading ? (
                    <tr><td colSpan={10} style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>Loading…</td></tr>
                  ) : logs.length === 0 ? (
                    <tr>
                      <td colSpan={10} style={{ textAlign: 'center', padding: '3rem' }}>
                        <ClipboardList size={36} color="#d1d5db" style={{ marginBottom: 8 }} />
                        <div style={{ color: '#9ca3af', fontSize: '0.88rem' }}>No visit logs found.</div>
                        <div style={{ color: '#d1d5db', fontSize: '0.8rem', marginTop: 4 }}>Try adjusting your filters.</div>
                      </td>
                    </tr>
                  ) : logs.map((log, i) => {
                    const sc = VISIT_STATUS_COLORS[log.visit_status] || { bg: '#f3f4f6', color: '#374151' };
                    const prods = Array.isArray(log.products_discussed) ? log.products_discussed : [];
                    // A visit log's customer can be a Lead (follow_up/payment) or, for "new
                    // customer" visits, a retail/corporate customer carried on the parent Visit.
                    const customerRef = log.lead?.id
                      ? { kind: 'lead', id: log.lead.id, name: log.lead.name, phone: log.lead.phone }
                      : log.visit?.raw_customer?.id
                        ? { kind: 'retail', id: log.visit.raw_customer.id, name: log.visit.raw_customer.name, phone: log.visit.raw_customer.phone }
                        : log.visit?.corporate_customer?.id
                          ? { kind: 'corporate', id: log.visit.corporate_customer.id, name: log.visit.corporate_customer.name, phone: log.visit.corporate_customer.phone }
                          : null;
                    return (
                      <tr key={log.id} style={{ borderBottom: '1px solid #f9fafb' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}
                      >
                        <td style={{ ...tdStyle, color: '#9ca3af', width: 40 }}>{(logPage - 1) * logLimit + i + 1}</td>
                        <td style={{ ...tdStyle, fontWeight: 600 }}>{customerRef?.name ?? '—'}</td>
                        <td style={{ ...tdStyle, color: '#6b7280', whiteSpace: 'nowrap' }}>{customerRef?.phone ?? '—'}</td>
                        <td style={tdStyle}>{log.user?.name ?? '—'}</td>
                        <td style={tdStyle}><Badge bg={sc.bg} color={sc.color}>{VISIT_STATUS_LABELS[log.visit_status] ?? log.visit_status}</Badge></td>
                        <td style={tdStyle}>{log.outcome ? (OUTCOME_LABELS[log.outcome] ?? log.outcome) : '—'}</td>
                        <td style={{ ...tdStyle, color: '#6b7280', fontSize: '0.8rem' }}>
                          {prods.length > 0 ? prods.map(p => p.name).join(', ') : '—'}
                        </td>
                        <td style={{ ...tdStyle, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#6b7280' }} title={log.note}>{log.note || '—'}</td>
                        <td style={{ ...tdStyle, whiteSpace: 'nowrap', color: '#6b7280', fontSize: '0.8rem' }}>{log.next_visit_date || '—'}</td>
                        <td style={{ ...tdStyle, whiteSpace: 'nowrap', color: '#9ca3af', fontSize: '0.8rem' }}>
                          {log.createdAt ? new Date(log.createdAt).toLocaleDateString('en-IN') : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <Pagination page={logPage} pages={logPages} limit={logLimit} onLimitChange={setLogLimit} onChange={setLogPage} />
        </div>
      )}

      {/* ── VISIT LIST TAB ────────────────────────────────────────────────────── */}
      {tab === 'visit-list' && (
        <div>
          {/* Visit List — filters merged into the same card as the table; all counts live in the badges/dropdowns below */}
          <div className="card" style={{ overflow: 'hidden' }}>
            <div className="card-header" style={{ flexWrap: 'wrap', gap: 8, alignItems: 'flex-start' }}>
              <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <MapPin size={15} color="#6b7280" />
                Visit List
                <span className="badge badge-gray">{vlCount}</span>
              </span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                <span className="badge badge-info">Follow-up (Leads) {vlLoading ? '…' : vlStats.leads ?? 0}</span>
                <span className="badge badge-success">New Customers {vlLoading ? '…' : vlStats.newCustomers ?? 0}</span>
                <span className="badge badge-orange">Debtors {vlLoading ? '…' : vlStats.debtors ?? 0}</span>
                <span className="badge badge-warning">Collection (Pending) {vlLoading ? '…' : vlStats.collectionPending ?? 0}</span>
                <span className="badge badge-danger">Not Ordering {vlLoading ? '…' : vlStats.notOrdering ?? 0}</span>
              </div>
            </div>

            {/* Filters row */}
            <div style={{ padding: '12px 16px 0' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', alignItems: 'flex-end' }}>
                <div style={{ flex: '1 1 150px', minWidth: 130 }}>
                  <FilterLabel>Salesperson</FilterLabel>
                  <select style={{ ...inp, width: '100%' }} value={vlSalesId} onChange={e => setVlSalesId(e.target.value)}>
                    <option value="">All</option>
                    {salesUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                <div style={{ flex: '1 1 130px', minWidth: 120 }}>
                  <FilterLabel>Type</FilterLabel>
                  <select style={{ ...inp, width: '100%' }} value={vlRetailSegment} onChange={e => setVlRetailSegment(e.target.value)}>
                    <option value="">All Types</option>
                    <option value="Dormant">Dormant ({vlStats.retailDormant ?? 0})</option>
                    <option value="XDifficult">XDifficult ({vlStats.retailDifficult ?? 0})</option>
                  </select>
                </div>
                <div style={{ flex: '1 1 130px', minWidth: 120 }}>
                  <FilterLabel>Customer Type</FilterLabel>
                  <select style={{ ...inp, width: '100%' }} value={vlCustomerType} onChange={e => setVlCustomerType(e.target.value)}>
                    <option value="">All ({vlStats.total ?? 0})</option>
                    <option value="retail">Retail ({vlStats.retailTotal ?? 0})</option>
                    <option value="corporate">Corporate ({vlStats.corporateTotal ?? 0})</option>
                    <option value="existing">Existing Client ({vlStats.existingTotal ?? 0})</option>
                  </select>
                </div>
                <div style={{ flex: '1 1 140px', minWidth: 130 }}>
                  <FilterLabel>Status</FilterLabel>
                  <select style={{ ...inp, width: '100%' }} value={vlStatus} onChange={e => setVlStatus(e.target.value)}>
                    <option value="">All ({vlStats.total ?? 0})</option>
                    <option value="pending">Pending ({vlStats.pending ?? 0})</option>
                    <option value="complete">Completed ({vlStats.completed ?? 0})</option>
                    <option value="in_progress">In Progress ({vlStats.inProgress ?? 0})</option>
                  </select>
                </div>
                <button onClick={fetchVisitList} disabled={vlLoading} className="btn btn-outline" style={{ padding: '7px 14px' }}>
                  <RefreshCw size={13} /> {vlLoading ? 'Loading…' : 'Refresh'}
                </button>
                {hasVlFilters && (
                  <button onClick={() => { setVlSalesId(''); setVlRetailSegment(''); setVlCustomerType(''); setVlStatus(''); }}
                    className="btn btn-outline" style={{ padding: '7px 14px' }}>
                    <X size={13} /> Clear
                  </button>
                )}
              </div>
              <div style={{ borderBottom: '1px solid #f3f4f6', marginTop: 12 }} />
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                    {['Salesperson', 'Customer', 'Customer Type', 'Visit Type', 'Status', 'Note', 'Last Remark', 'Actions'].map(h => (
                      <th key={h} style={thStyle}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {vlLoading ? (
                    <tr><td colSpan={8} style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>Loading…</td></tr>
                  ) : vlVisits.length === 0 ? (
                    <tr>
                      <td colSpan={8} style={{ textAlign: 'center', padding: '3rem' }}>
                        <MapPin size={36} color="#d1d5db" style={{ marginBottom: 8 }} />
                        <div style={{ color: '#9ca3af', fontSize: '0.88rem' }}>
                          {hasVlFilters ? 'No visits match the current filters.' : 'No visit data yet.'}
                        </div>
                        <div style={{ color: '#d1d5db', fontSize: '0.8rem', marginTop: 4 }}>
                          {hasVlFilters ? 'Try clearing the filters.' : 'Run the visit queue job to populate visits.'}
                        </div>
                      </td>
                    </tr>
                  ) : vlVisits.map(v => {
                    const customerName = v.customer?.name ?? v.raw_customer?.name ?? v.corporate_customer?.name ?? '—';
                    const customerRef = v.raw_customer?.id
                      ? { kind: 'retail', id: v.raw_customer.id, name: v.raw_customer.name, phone: v.raw_customer.phone }
                      : v.corporate_customer?.id
                        ? { kind: 'corporate', id: v.corporate_customer.id, name: v.corporate_customer.name, phone: v.corporate_customer.phone }
                        : v.customer?.id
                          ? { kind: 'lead', id: v.customer.id, name: v.customer.name, phone: v.customer.phone }
                          : null;
                    const custType = v.raw_customer_list_id ? { label: 'Retail', bg: '#e0f2fe', color: '#0369a1' }
                      : v.corporate_customer_id ? { label: 'Corporate', bg: '#ede9fe', color: '#6d28d9' }
                      : { label: 'Existing Customer', bg: '#f3f4f6', color: '#6b7280' };
                    const typeInfo = { follow_up: { label: 'Follow-up', bg: '#dbeafe', color: '#1d4ed8' }, new: { label: 'New', bg: '#ccfbf1', color: '#0f766e' }, payment: { label: 'Debtor', bg: '#fed7aa', color: '#92400e' } }[v.type] || { label: v.type, bg: '#f3f4f6', color: '#374151' };
                    const statusInfo = { pending: { label: 'Pending', bg: '#fef9c3', color: '#854d0e' }, complete: { label: 'Completed', bg: '#dcfce7', color: '#15803d' }, in_progress: { label: 'In Progress', bg: '#ede9fe', color: '#6d28d9' } }[v.status] || { label: v.status, bg: '#f3f4f6', color: '#374151' };
                    return (
                      <tr key={v.id} style={{ borderBottom: '1px solid #f9fafb' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}
                      >
                        <td style={tdStyle}>{v.sales?.name ?? '—'}</td>
                        <td style={{ ...tdStyle, fontWeight: 600 }}>{customerName}</td>
                        <td style={tdStyle}><Badge bg={custType.bg} color={custType.color}>{custType.label}</Badge></td>
                        <td style={tdStyle}><Badge bg={typeInfo.bg} color={typeInfo.color}>{typeInfo.label}</Badge></td>
                        <td style={tdStyle}><Badge bg={statusInfo.bg} color={statusInfo.color}>{statusInfo.label}</Badge></td>
                        <td style={{ ...tdStyle, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#6b7280', fontSize: '0.8rem' }} title={v.note || ''}>
                          {v.note || '—'}
                        </td>
                        <td style={{ ...tdStyle, maxWidth: 220 }}>
                          <button
                            onClick={() => setRemarksModal(v)}
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left',
                              color: v.last_remark ? '#2563eb' : '#9ca3af', fontSize: '0.82rem',
                              display: 'block', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}
                            title={v.last_remark || 'No remark yet — click to view visit history'}
                          >
                            {v.last_remark || 'No remark yet'}
                          </button>
                        </td>
                        <td style={tdStyle}>
                          {customerRef && (
                            <button
                              onClick={() => { setLogCustomerFilter(customerRef); setLogPage(1); setTab('logs'); }}
                              title={`View all visit logs for ${customerRef.name || 'this customer'}`}
                              style={{ padding: '4px 10px', border: '1px solid #bfdbfe', background: '#eff6ff', color: '#2563eb', borderRadius: 6, cursor: 'pointer', fontSize: '0.76rem', fontWeight: 600, whiteSpace: 'nowrap' }}
                            >
                              View Logs
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <Pagination page={vlPage} pages={vlPages} limit={vlLimit} onLimitChange={setVlLimit} onChange={setVlPage} />

          {/* Reassign Modal */}
          {reassignModal && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onClick={() => { setReassignModal(false); setReassignResult(null); }}
            >
              <div style={{ background: '#fff', borderRadius: 14, padding: '24px', width: 420, maxWidth: '92vw', boxShadow: '0 20px 60px rgba(0,0,0,.2)' }} onClick={e => e.stopPropagation()}>
                <div style={{ fontWeight: 800, fontSize: '0.97rem', marginBottom: 4 }}>Reassign Visits</div>
                <div style={{ fontSize: '0.83rem', color: '#6b7280', marginBottom: 20 }}>Move all pending visits from one salesperson to another.</div>

                {reassignResult && (
                  <div style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 16, background: reassignResult.success ? '#f0fdf4' : '#fef2f2', border: `1px solid ${reassignResult.success ? '#bbf7d0' : '#fecaca'}`, color: reassignResult.success ? '#15803d' : '#dc2626', fontSize: '0.82rem' }}>
                    {reassignResult.success ? '✓ ' : '✕ '}{reassignResult.message}
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div>
                    <FilterLabel>From (absent salesperson)</FilterLabel>
                    <select style={{ ...inp, width: '100%' }} value={fromUser} onChange={e => setFromUser(e.target.value)}>
                      <option value="">— Select —</option>
                      {salesUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <FilterLabel>To (replacement salesperson)</FilterLabel>
                    <select style={{ ...inp, width: '100%' }} value={toUser} onChange={e => setToUser(e.target.value)}>
                      <option value="">— Select —</option>
                      {salesUsers.filter(u => u.id.toString() !== fromUser).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <FilterLabel>Date</FilterLabel>
                    <input type="date" style={{ ...inp, width: '100%' }} value={reassignDate} onChange={e => setReassignDate(e.target.value)} />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
                  <button onClick={() => { setReassignModal(false); setReassignResult(null); }}
                    style={{ flex: 1, padding: '9px', borderRadius: 7, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontWeight: 500, fontSize: '0.87rem', color: '#374151' }}>
                    Cancel
                  </button>
                  <button onClick={handleReassign} disabled={reassignLoading || !fromUser || !toUser}
                    style={{ flex: 2, padding: '9px', borderRadius: 7, border: 'none', background: 'var(--primary)', opacity: (!fromUser || !toUser || reassignLoading) ? 0.5 : 1, color: '#fff', cursor: (!fromUser || !toUser || reassignLoading) ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.87rem' }}>
                    {reassignLoading ? 'Reassigning…' : 'Reassign Visits'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {addModal && (
        <AddVisitModal
          salesUsers={salesUsers}
          onClose={() => setAddModal(false)}
          onSuccess={(msg) => {
            setAddModal(false);
            setAddMsg(msg);
            fetchVisitList();
            setTimeout(() => setAddMsg(''), 5000);
          }}
        />
      )}

      {remarksModal && (
        <VisitRemarksModal
          visit={remarksModal}
          onClose={() => setRemarksModal(null)}
        />
      )}
    </div>
  );
}
